/**
 * Reads the campaign attribution captured on zamino.com and holds it for the
 * signup calls.
 *
 * Captured at app boot, not at SignUpPage mount. SignInPage hands OAuth
 * signups to the wizard via `navigate('/signup', { state })` — a client-side
 * navigation that has already discarded the query string — so a mount-time
 * read would lose the param fallback on that entire path.
 *
 * See bizguard-backend/docs/CAMPAIGN_TRACKING_SPEC.md §5.3.
 */

const COOKIE_NAME = 'zam_attr';
const STORAGE_KEY = 'zam_attr';
const MAX_VALUE_LENGTH = 500;

const ATTRIBUTION_PARAMS = [
  'gclid',
  'wbraid',
  'gbraid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

type AttributionParam = (typeof ATTRIBUTION_PARAMS)[number];

export interface Attribution {
  gclid?: string;
  wbraid?: string;
  gbraid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  landing_path?: string;
  referrer?: string;
  captured_at?: string;
}

function clamp(value: string): string {
  return value.slice(0, MAX_VALUE_LENGTH);
}

function readCookie(): Attribution | null {
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${COOKIE_NAME}=`));
  if (!match) return null;

  try {
    const parsed: unknown = JSON.parse(
      decodeURIComponent(match.slice(COOKIE_NAME.length + 1)),
    );
    return parsed && typeof parsed === 'object' ? (parsed as Attribution) : null;
  } catch {
    return null;
  }
}

function readQuery(): Attribution | null {
  const params = new URLSearchParams(window.location.search);
  const captured: Attribution = {};

  for (const key of ATTRIBUTION_PARAMS) {
    const value = params.get(key);
    if (value) captured[key as AttributionParam] = clamp(value);
  }
  if (Object.keys(captured).length === 0) return null;

  captured.landing_path = clamp(window.location.pathname);
  captured.captured_at = new Date().toISOString();
  return captured;
}

/**
 * Call once from main.tsx. The cookie is authoritative — it survives the whole
 * journey and already applied the last-paid-click rule on the marketing side.
 * Query params are the fallback for a cookie-blocked browser.
 */
export function captureAttribution(): void {
  try {
    const attribution = readCookie() ?? readQuery();
    if (!attribution) return;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // Private browsing, blocked storage, malformed cookie — attribution is
    // never worth breaking app boot over.
  }
}

/** The attribution to attach to a signup call, or null if this visit has none. */
export function getAttribution(): Attribution | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const parsed: unknown = JSON.parse(stored);
    return parsed && typeof parsed === 'object' ? (parsed as Attribution) : null;
  } catch {
    return null;
  }
}
